import { LogOut, Menu, Settings, X } from "lucide-react";

// Use direct paths - Vite handles cache busting automatically
const logoLight = "/assets/novaroute-logo-light.svg";
const wordmarkLight = "/assets/novaroute-wordmark-light.svg";

import {
	Button,
	Sheet,
	SheetContent,
	SheetTitle,
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@ancatag/at-design";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "../../context/AuthContext";
import { useMobile } from "../../hooks/useMobile";
import { useSwipeGesture } from "../../hooks/useSwipeGesture";
import { cn } from "../../lib/utils";
import { ProjectSelector } from "../projects/ProjectSelector";
import { getNavSections } from "./nav";

const SIDEBAR_STORAGE_KEY = "nova-route-sidebar-collapsed";

export function Layout({ children }: { children: React.ReactNode }) {
	const [location] = useLocation();
	const { logout, user } = useAuth();
	const [sheetOpen, setSheetOpen] = useState(false);
	const [isCollapsed, setIsCollapsed] = useState(false);
	const { isMobile } = useMobile();

	useEffect(() => {
		const storedState = localStorage.getItem(SIDEBAR_STORAGE_KEY);
		if (storedState) setIsCollapsed(storedState === "true");
	}, []);

	useEffect(() => {
		localStorage.setItem(SIDEBAR_STORAGE_KEY, String(isCollapsed));
	}, [isCollapsed]);

	const navSections = useMemo(() => getNavSections(user?.role), [user?.role]);

	const flatNav = useMemo(
		() => navSections.flatMap((section) => section.items),
		[navSections],
	);

	const currentPageTitle = (() => {
		// Find exact match first
		const exactMatch = flatNav.find((item) => location === item.href);
		if (exactMatch) return exactMatch.name;

		// Find most specific match (longest href that matches)
		const matches = flatNav
			.filter((item) => location.startsWith(`${item.href}/`))
			.sort((a, b) => b.href.length - a.href.length); // Sort by length descending

		return matches[0]?.name || "Dashboard";
	})();

	const handleLogout = async () => {
		await logout();
	};

	// Refs for click outside detection
	const sidebarRef = useRef<HTMLElement>(null);
	const mainContentRef = useRef<HTMLDivElement>(null);

	// Handle click outside sidebar to collapse (only when expanded and on desktop)
	useEffect(() => {
		if (isCollapsed || isMobile) return; // Don't collapse if already collapsed or on mobile

		const handleClickOutside = (event: MouseEvent | TouchEvent) => {
			const target = event.target as Node;
			const targetElement = target as Element;

			// FIRST: Check if ANY dialog exists in DOM (SIMPLE AND RELIABLE)
			// This catches all dialogs regardless of portal rendering or event path
			const anyDialog =
				document.querySelector('[role="dialog"]') ||
				document.querySelector("[data-nova-dialog]");

			if (anyDialog) {
				return; // Don't collapse if any dialog exists
			}

			// SECOND: Use composedPath to check event path (for portal-rendered elements)
			// This is a backup check in case the first check missed something
			const path =
				"composedPath" in event ? (event as MouseEvent).composedPath() : [];

			// Check if ANY element in the path is a dialog or inside a dialog
			let isInDialog = false;
			for (const el of path) {
				if (!el || typeof el !== "object") continue;
				const element = el as Element;

				// Check if element itself is a dialog
				if (element.matches && typeof element.matches === "function") {
					if (
						element.matches('[role="dialog"]') ||
						element.matches("[data-nova-dialog]")
					) {
						isInDialog = true;
						break;
					}
				}

				// Check if element is inside a dialog (works even in portals)
				if (element.closest && typeof element.closest === "function") {
					const dialogParent =
						element.closest('[role="dialog"]') ||
						element.closest("[data-nova-dialog]");
					if (dialogParent) {
						isInDialog = true;
						break;
					}
				}
			}

			// Also check target directly (for non-portal cases)
			if (!isInDialog && targetElement) {
				const dialogParent =
					targetElement.closest?.('[role="dialog"]') ||
					targetElement.closest?.("[data-nova-dialog]");
				if (dialogParent) {
					isInDialog = true;
				}
			}

			// Check if clicking a button - if so, check if it's in a dialog
			const isButton =
				targetElement?.tagName === "BUTTON" ||
				!!targetElement?.closest?.("button");
			if (isButton && !isInDialog) {
				const buttonElement =
					targetElement?.tagName === "BUTTON"
						? targetElement
						: targetElement?.closest?.("button");
				if (buttonElement) {
					const buttonInDialog =
						buttonElement.closest?.('[role="dialog"]') ||
						buttonElement.closest?.("[data-nova-dialog]");
					if (buttonInDialog) {
						isInDialog = true;
					}
				}
			}

			// SECOND: Check if ANY dialog exists in DOM (backup check)
			// This catches dialogs that might not be in the event path
			if (!isInDialog) {
				const anyDialog =
					document.querySelector('[role="dialog"]') ||
					document.querySelector("[data-nova-dialog]");
				if (anyDialog) {
					isInDialog = true;
				}
			}

			if (isInDialog) {
				return; // Don't collapse if clicking inside a dialog
			}

			const inSidebar = sidebarRef.current?.contains(target);
			const inMainContent = mainContentRef.current?.contains(target);

			if (
				sidebarRef.current &&
				!inSidebar &&
				mainContentRef.current &&
				inMainContent
			) {
				setIsCollapsed(true);
			}
		};

		// Add listeners for click events (not mousedown)
		// Using 'click' instead of 'mousedown' allows button onClick handlers to fire first
		// Using bubbling phase (false) so button onClick fires before this handler
		// Button handlers use stopPropagation, so if they fire first, this won't run
		document.addEventListener("click", handleClickOutside, false); // Bubbling phase
		document.addEventListener("touchstart", handleClickOutside, false);
		return () => {
			document.removeEventListener("click", handleClickOutside, false);
			document.removeEventListener("touchstart", handleClickOutside, false);
		};
	}, [isCollapsed, isMobile]);

	// Handle click on collapsed sidebar to expand (desktop only)
	const handleSidebarClick = (e: React.MouseEvent) => {
		if (isMobile) return; // Don't handle clicks on mobile (sidebar is hidden)

		// Only expand if clicking on the sidebar itself, not on navigation links
		const target = e.target as HTMLElement;
		const isNavLink = target.closest("a") || target.closest("button");

		if (isCollapsed && !isNavLink) {
			setIsCollapsed(false);
		}
	};

	// Swipe gesture for mobile drawer
	const drawerSwipeRef = useSwipeGesture({
		onSwipeLeft: () => setSheetOpen(false),
	});

	const NavLink = ({
		item,
		collapsed = false,
		onClick,
	}: {
		item: (typeof flatNav)[0];
		collapsed?: boolean;
		onClick?: () => void;
	}) => {
		const Icon = item.icon;
		const isActive =
			location === item.href || location.startsWith(`${item.href}/`);

		const linkContent = (
			<Link href={item.href} onClick={onClick}>
				<span
					className={cn(
						"flex items-center gap-3 rounded-md px-3 py-2.5 md:py-2 text-sm font-medium transition-colors touch-manipulation",
						collapsed && "justify-center px-2",
						isActive
							? "bg-primary text-primary-foreground"
							: "text-muted-foreground hover:bg-muted hover:text-foreground active:bg-muted",
					)}
				>
					<Icon className="h-4 w-4 shrink-0" />
					{!collapsed && <span>{item.name}</span>}
				</span>
			</Link>
		);

		if (collapsed) {
			return (
				<Tooltip>
					<TooltipTrigger asChild>{linkContent}</TooltipTrigger>
					<TooltipContent side="right" sideOffset={8}>
						{item.name}
					</TooltipContent>
				</Tooltip>
			);
		}

		return linkContent;
	};

	return (
		<div className="flex min-h-screen bg-background overflow-x-hidden">
			{/* Desktop Sidebar */}
			<aside
				ref={sidebarRef}
				onClick={handleSidebarClick}
				className={cn(
					"fixed inset-y-0 left-0 z-50 hidden flex-col border-r border-border bg-card md:flex transition-all duration-200",
					isCollapsed && "cursor-pointer",
				)}
			>
				{/* Logo */}
				<div
					className={cn(
						"flex h-14 items-center border-b border-border px-4",
						isCollapsed && "hover:bg-muted/50 transition-colors",
					)}
				>
					<div className="flex items-center gap-2">
						<div className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-card">
							<img src={logoLight} alt="Nova-route" className="h-7 w-7" />
						</div>
						{!isCollapsed && (
							<img src={wordmarkLight} alt="Nova-route" className="h-6" />
						)}
					</div>
				</div>
				{/* Navigation */}
				<nav className="flex-1 overflow-y-auto p-3">
					<TooltipProvider delayDuration={300}>
						<div className="space-y-6">
							{navSections.map((section) => (
								<div key={section.title}>
									{!isCollapsed && (
										<p className="mb-2 px-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
											{section.title}
										</p>
									)}
									<div className="space-y-1">
										{section.items.map((item) => (
											<NavLink
												key={item.name}
												item={item}
												collapsed={isCollapsed}
											/>
										))}
									</div>
								</div>
							))}
						</div>
					</TooltipProvider>
				</nav>

				{/* Footer */}
				<div className="border-t border-border p-3">
					<div className="space-y-2">
						<TooltipProvider delayDuration={300}>
							{isCollapsed ? (
								<Tooltip>
									<TooltipTrigger asChild>
										<button
											type="button"
											onClick={handleLogout}
											className="flex w-full items-center justify-center rounded-md px-2 py-2.5 md:py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground touch-manipulation active:bg-muted"
										>
											<LogOut className="h-4 w-4" />
										</button>
									</TooltipTrigger>
									<TooltipContent side="right" sideOffset={8}>
										Logout
									</TooltipContent>
								</Tooltip>
							) : (
								<button
									type="button"
									onClick={handleLogout}
									className="flex w-full items-center gap-3 rounded-md px-3 py-2.5 md:py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground touch-manipulation active:bg-muted"
								>
									<LogOut className="h-4 w-4" />
									<span>Logout</span>
								</button>
							)}
						</TooltipProvider>
					</div>
				</div>
			</aside>

			{/* Main Content */}
			<div
				ref={mainContentRef}
				className={cn(
					"flex flex-1 flex-col transition-[margin] duration-200 w-full",
					"ml-0", // No margin on mobile
					isCollapsed ? "md:ml-16" : "md:ml-56",
				)}
			>
				{/* Top Header */}
				<header className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-border bg-background/95 px-3 sm:px-4 md:px-6 backdrop-blur supports-[backdrop-filter]:bg-background/60">
					<div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
						{/* Mobile Menu Button */}
						<Button
							variant="ghost"
							size="icon"
							className="h-10 w-10 md:hidden shrink-0 touch-manipulation"
							onClick={() => setSheetOpen(true)}
							aria-label="Open menu"
						>
							<Menu className="h-5 w-5" />
						</Button>
						<h1 className="text-sm sm:text-base md:text-lg font-semibold truncate">
							{currentPageTitle}
						</h1>
					</div>
					<div className="flex items-center gap-0.5 sm:gap-1 md:gap-2 shrink-0">
						{/* Hide project selector for super admins - they have access to all projects */}
						{user?.role !== "superAdmin" && (
							<div className="hidden sm:block">
								<ProjectSelector />
							</div>
						)}
						<Button
							variant="ghost"
							size="icon"
							className="h-10 w-10 touch-manipulation"
						>
							<Settings className="h-5 w-5" />
						</Button>
					</div>
				</header>

				{/* Page Content */}
				<main className="flex-1 p-3 sm:p-4 md:p-6 overflow-x-hidden">
					{children}
				</main>
			</div>

			{/* Mobile Drawer */}
			<Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
				<SheetContent side="left" className="w-72 p-0">
					<SheetTitle className="sr-only">Navigation Menu</SheetTitle>
					<div
						ref={drawerSwipeRef as React.RefObject<HTMLDivElement>}
						className="flex h-full flex-col"
					>
						{/* Mobile Header */}
						<div className="flex h-14 items-center justify-between border-b border-border px-4">
							<div className="flex items-center gap-2">
								<img src={wordmarkLight} alt="Nova-route" className="h-6" />
							</div>
							<Button
								variant="ghost"
								size="icon"
								className="h-10 w-10"
								onClick={() => setSheetOpen(false)}
								aria-label="Close menu"
							>
								<X className="h-5 w-5" />
							</Button>
						</div>

						{/* Mobile Navigation */}
						<nav className="flex-1 overflow-y-auto p-3">
							<div className="space-y-6">
								{navSections.map((section) => (
									<div key={section.title}>
										<p className="mb-2 px-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
											{section.title}
										</p>
										<div className="space-y-1">
											{section.items.map((item) => (
												<NavLink
													key={item.name}
													item={item}
													onClick={() => setSheetOpen(false)}
												/>
											))}
										</div>
									</div>
								))}
							</div>
						</nav>

						{/* Mobile Footer */}
						<div className="border-t border-border p-3">
							<button
								type="button"
								onClick={() => {
									setSheetOpen(false);
									handleLogout();
								}}
								className="flex w-full items-center gap-3 rounded-md px-3 py-3 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground touch-manipulation active:bg-muted"
							>
								<LogOut className="h-4 w-4" />
								<span>Logout</span>
							</button>
						</div>
					</div>
				</SheetContent>
			</Sheet>
		</div>
	);
}

export default Layout;
